import React, {useEffect, useRef, useState} from 'react';
import {resolveAppearance} from '../utils/uiUtils';
import {IconLink, IconPlus, IconScan, IconStar, IconStarFilled, IconX} from '@tabler/icons-react';
import {Screen, Button} from '../types';
import {storage} from '../utils/localStorage';
import {ScreenNotificationButton} from './ScreenNotificationButton';
import {ScreenNotification} from '../hooks/useScreenNotifications';
import {BarcodeScanner} from './BarcodeScanner';

interface ScreenDataTableHeaderProps {
    screen: Screen;
    search: string;
    onSearchChange: (value: string) => void;
    hasSearchableColumns: boolean;
    hasBarcodeSearch: boolean;
    filterableColumnsCount: number;
    showFilters: boolean;
    onToggleFilters: () => void;
    savedFilters: any[];
    selectedUserFilter: string | null;
    showSavedFiltersDropdown: boolean;
    onToggleSavedFiltersDropdown: () => void;
    onFilterSelect: (filter: any) => void;
    onClearFilter: () => void;
    onSetFilterDefault?: (filter: any, isDefault: boolean) => void;
    showCreateDropdown: boolean;
    onToggleCreateDropdown: () => void;
    onCreateClick: (form: any) => void;
    onOpenCreateForm?: (form: any) => void;
    /** Many-to-many link screen embedded in a form: pick existing records instead of creating link rows one by one. */
    onAssignExisting?: () => void;
    embedded?: boolean;
    notifications: ScreenNotification[];
    unreadCount: number;
    onDismissNotification: (id: string) => void;
    onClearAllNotifications: () => void;
    onMarkAllAsRead: () => void;
    editable: boolean;
    autoFocusSearch?: boolean;
    onSearchSubmit?: () => void;
    pageSize?: number;
    onPageSizeChange?: (size: number) => void;
    pageSizeOptions?: { limit: number }[];
    embeddedTitle?: string;
    screenButtons?: Button[];
    onScreenButtonClick?: (button: Button, e: React.MouseEvent) => void;
}

export const ScreenDataTableHeader: React.FC<ScreenDataTableHeaderProps> = ({
    screen,
    search,
    onSearchChange,
    hasSearchableColumns,
    hasBarcodeSearch,
    filterableColumnsCount,
    showFilters,
    onToggleFilters,
    savedFilters,
    selectedUserFilter,
    showSavedFiltersDropdown,
    onToggleSavedFiltersDropdown,
    onFilterSelect,
    onClearFilter,
    onSetFilterDefault,
    showCreateDropdown,
    onToggleCreateDropdown,
    onCreateClick,
    onOpenCreateForm,
    onAssignExisting,
    embedded = false,
    notifications,
    unreadCount,
    onDismissNotification,
    onClearAllNotifications,
    onMarkAllAsRead,
    editable = true,
    autoFocusSearch = false,
    onSearchSubmit,
    pageSize,
    onPageSizeChange,
    pageSizeOptions,
    embeddedTitle,
    screenButtons,
    onScreenButtonClick,
}) => {
    const defaultOptions = [{limit: 20}, {limit: 50}, {limit: 100}];
    const opts = pageSizeOptions && pageSizeOptions.length > 0 ? pageSizeOptions : defaultOptions;
    const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (autoFocusSearch && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [autoFocusSearch]);

    const handleBarcodeScan = (value: string) => {
        setShowBarcodeScanner(false);

        setTimeout(() => {
            onSearchChange(value);
            if (!embedded) {
                const currentSearchKey = `search_${screen.id}`;
                storage.setItem(currentSearchKey, value);
            }
        }, 100);
    };

    // Header is always rendered — filter button is always visible.

    return (
        <div className="v5-controls">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 w-100">
                {embeddedTitle && (
                    <h3 className="mb-0 me-md-2 flex-shrink-0">{embeddedTitle}</h3>
                )}
                {hasSearchableColumns && (
                    <div className="flex-fill" style={{maxWidth: hasSearchableColumns ? '400px' : 'none'}}>
                        <div className="input-group input-group-flat">
                            <span className="input-group-text">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon">
                                    <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                                    <path d="M21 21l-6 -6"></path>
                                </svg>
                            </span>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder="Vyhľadať..."
                                value={search}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && onSearchSubmit) {
                                        e.preventDefault();
                                        onSearchSubmit();
                                    }
                                }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onSearchChange(value);

                                    if (!embedded) {
                                        const currentSearchKey = `search_${screen.id}`;
                                        if (value) {
                                            storage.setItem(currentSearchKey, value);
                                        } else {
                                            storage.removeItem(currentSearchKey);
                                        }
                                    }
                                }}
                            />
                            {search && (
                                <span
                                    className="input-group-text"
                                    onClick={() => {
                                        onSearchChange('');
                                        if (!embedded) {
                                            storage.removeItem(`search_${screen.id}`);
                                        }
                                    }}
                                    title="Vymazať vyhľadávanie"
                                    style={{cursor: 'pointer', padding: '0 0.75rem'}}
                                >
                                    <IconX size={18} />
                                </span>
                            )}
                            {hasBarcodeSearch && (
                                <span
                                    className="input-group-text bg-primary text-white"
                                    onClick={() => setShowBarcodeScanner(true)}
                                    title="Skenovať čiarový kód"
                                    style={{cursor: 'pointer', padding: '0 0.75rem'}}
                                >
                                    <IconScan size={20} />
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <div className={`d-flex flex-column flex-md-row gap-2 ${!hasSearchableColumns ? 'ms-auto' : ''}`}>
                        {filterableColumnsCount > 0 && (
                            <button
                                className="btn btn-outline-primary"
                                onClick={onToggleFilters}
                            >
                                Filtre {showFilters ? '▲' : '▼'}
                            </button>
                        )}

                        {notifications.length > 0 && (
                            <ScreenNotificationButton
                                notifications={notifications}
                                unreadCount={unreadCount}
                                onDismiss={onDismissNotification}
                                onClearAll={onClearAllNotifications}
                                onMarkAllAsRead={onMarkAllAsRead}
                            />
                        )}

                        {savedFilters.length > 0 && (
                            <div className="dropdown" style={{position: 'relative'}}>
                                <button
                                    className="btn btn-outline-warning dropdown-toggle w-100"
                                    onClick={onToggleSavedFiltersDropdown}
                                >
                                    {selectedUserFilter
                                        ? savedFilters.find((f: any) => f.id === selectedUserFilter)?.name
                                        : 'Uložené filtre'}
                                </button>
                                {showSavedFiltersDropdown && (
                                    <div className="dropdown-menu show" style={{position: 'absolute', minWidth: '200px'}}>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => {
                                                onClearFilter();
                                                onToggleSavedFiltersDropdown();
                                            }}
                                        >
                                            Zrušiť filter
                                        </button>
                                        {savedFilters.map((filter: any) => (
                                            <button
                                                key={filter.id}
                                                className="dropdown-item"
                                                onClick={() => {
                                                    onFilterSelect(filter);
                                                    onToggleSavedFiltersDropdown();
                                                }}
                                            >
                                                {filter.name}
                                                {filter.isDefault && (
                                                    <IconStarFilled size={14} className="text-warning ms-2" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {onAssignExisting && (
                            <button
                                type="button"
                                data-e2e="assign-existing"
                                className="btn btn-primary"
                                onClick={onAssignExisting}
                                title="Priradiť existujúci"
                            >
                                <IconLink size={16} className="me-1"/>
                                Priradiť
                            </button>
                        )}

                        {!onAssignExisting && editable && screen.createForms && screen.createForms.length > 0 && onOpenCreateForm && (
                            <>
                                {screen.createForms.length === 1 ? (
                                    <button
                                        data-e2e={`create:${screen.createForms[0].id}`}
                                        className="btn btn-primary"
                                        onClick={() => onCreateClick(screen.createForms[0])}
                                    >
                                        <IconPlus size={16} className="me-1"/>
                                        Pridať
                                    </button>
                                ) : (
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-primary dropdown-toggle w-100"
                                            onClick={onToggleCreateDropdown}
                                        >
                                            <IconPlus size={16} className="me-1"/>
                                            Pridať
                                        </button>
                                        {showCreateDropdown && (
                                            <div className="dropdown-menu show">
                                                {screen.createForms.map((form: any) => (
                                                    <button
                                                        key={form.id}
                                                        data-e2e={`create:${form.id}`}
                                                        className="dropdown-item"
                                                        onClick={() => onCreateClick(form)}
                                                    >
                                                        {form.displayName}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {screenButtons && screenButtons.length > 0 && onScreenButtonClick && (
                            screenButtons
                                .slice()
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map((button) => (
                                    <button
                                        key={button.id || button.url}
                                        type="button"
                                        data-e2e={`btn:${button.id || button.url}`}
                                        className={`btn ${resolveAppearance(button.appearance, 'button').className || 'btn-outline-primary'}`}
                                        style={resolveAppearance(button.appearance, 'button').style}
                                        onClick={(e) => onScreenButtonClick(button, e)}
                                        title={button.displayName}
                                    >
                                        {button.displayName}
                                    </button>
                                ))
                        )}
                </div>
            </div>

            {hasBarcodeSearch && (
                <BarcodeScanner
                    show={showBarcodeScanner}
                    onHide={() => setShowBarcodeScanner(false)}
                    onScan={handleBarcodeScan}
                />
            )}
        </div>
    );
};
